import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { useApolloClient } from '@apollo/client';
import {
  discardFailedTrackedQuery,
  FailedTrackedQuery,
  requeueFailedTrackedQuery,
  requestReplay,
} from '@bilderit/offline-link';
import { ChevronDown, AlertTriangle, Trash2, RotateCcw } from 'lucide-react';
import { useFailedTrackedQueries } from '@/hooks/useFailedTrackedQueries';
import {
  ADD_CHECKLIST_OPERATION,
  describeFailedChecklistSend,
  failedChecklistSendId,
} from '@/modules/checklists/failedChecklistSends';
import { removeChecklistFromCache } from '@/modules/checklists/MyChecklistsSchema';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/utils/utils';

type OperationDescription = {
  label: string;
  /** What identifies the record among others of the same kind. */
  detail?: string;
};

// What to call each entry this screen is likely to see: the daily report (see
// useAddOptimisticOfflineUsageReport.tsx) and the checklist (see
// useSubmitChecklist.ts, which stores the optimistic Checklist the detail
// reads). Anything else falls back to the raw operationName.
const describeOperation = (item: FailedTrackedQuery): OperationDescription => {
  switch (item.operationName) {
    case 'AddDailyReportOffline':
      return { label: 'Parte diario' };
    case ADD_CHECKLIST_OPERATION:
      return {
        label: 'Checklist',
        detail: describeFailedChecklistSend(item),
      };
    default:
      return { label: item.operationName };
  }
};

const formatFailedAt = (failedAt: string): string => {
  try {
    return format(new Date(failedAt), "d MMM yyyy 'a las' HH:mm", {
      locale: es,
    });
  } catch {
    return failedAt;
  }
};

/**
 * Surfaces mutations the offline queue could not deliver because the server
 * permanently rejected them (e.g. an authz denial) — see
 * OfflineMutationLoader/OfflineLink `moveTrackedQueryToFailed`. Without this,
 * a rejected record disappeared silently; this makes it visible and lets the
 * operator either discard it or reintentar (requeue + request an immediate
 * replay) once they understand why it failed. Discarding a checklist also
 * takes its pending row out of "Mis checklists".
 *
 * Renders nothing when there is nothing failed for the current identity.
 */
export const FailedOfflineMutations: React.FC = () => {
  const client = useApolloClient();
  const failed = useFailedTrackedQueries();
  const [open, setOpen] = React.useState(false);

  const handleDiscard = React.useCallback(
    (item: FailedTrackedQuery) => {
      discardFailedTrackedQuery(item.context.id)
        .then(() => {
          // A checklist's pending row lives only in the cache, so the
          // discarded send takes it away with it.
          if (item.operationName !== ADD_CHECKLIST_OPERATION) return;
          const checklistId = failedChecklistSendId(item);
          if (checklistId !== undefined) {
            removeChecklistFromCache(client, checklistId);
          }
        })
        .catch((err) => console.error('discardFailedTrackedQuery failed', err));
    },
    [client]
  );

  if (failed.length === 0) return null;

  return (
    <div className="w-full border-b border-destructive/30 bg-destructive/10 text-destructive">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-4 py-2 text-sm font-medium"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="size-4 shrink-0" />
              {failed.length === 1
                ? '1 registro no se pudo enviar'
                : `${failed.length} registros no se pudieron enviar`}
            </span>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="divide-y divide-destructive/20">
            {failed.map((item) => (
              <FailedOfflineMutationItem
                key={String(item.context.id)}
                item={item}
                onDiscard={handleDiscard}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const FailedOfflineMutationItem: React.FC<{
  item: FailedTrackedQuery;
  onDiscard: (item: FailedTrackedQuery) => void;
}> = ({ item, onDiscard }) => {
  const { label, detail } = describeOperation(item);
  const [confirming, setConfirming] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);

  const handleRetry = React.useCallback(() => {
    setRetrying(true);
    requeueFailedTrackedQuery(item.context.id)
      .then(() => {
        requestReplay();
        toast('Reintentando envío…');
      })
      .catch((err: unknown) => {
        console.error('requeueFailedTrackedQuery failed', err);
        setRetrying(false);
      });
    // No `finally` re-enabling the button on success: once requeued, the
    // entry leaves the failed store and this item unmounts via the
    // useFailedTrackedQueries re-read — nothing left to re-enable. If
    // the server rejects it again (or it's offline), it reappears through
    // that same re-read with a fresh message/queued state.
  }, [item.context.id]);

  return (
    <li className="flex flex-col gap-1 bg-background/40 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">
          {formatFailedAt(item.failure.failedAt)}
        </span>
      </div>
      {detail && <p className="text-muted-foreground">{detail}</p>}
      <p className="text-destructive">{item.failure.message}</p>
      <div className="flex justify-end gap-2 pt-1">
        {confirming ? (
          <>
            <span className="self-center text-xs text-muted-foreground">
              ¿Descartar este registro?
            </span>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={() => {
                setConfirming(false);
                onDiscard(item);
              }}
            >
              Sí, descartar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setConfirming(false)}
            >
              No
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-foreground"
              onClick={handleRetry}
              disabled={retrying}
            >
              <RotateCcw className="size-4" />
              Reintentar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive-outline"
              onClick={() => setConfirming(true)}
              disabled={retrying}
            >
              <Trash2 className="size-4" />
              Descartar
            </Button>
          </>
        )}
      </div>
    </li>
  );
};

export default FailedOfflineMutations;
